import Axios from 'axios'
import {
  applyAuthSession,
  applyDistributionSession,
  clearAuthSession,
  getToken,
  hasAuthSession,
  normalizeAuthHeader
} from './auth'
import { isEmbedMode, readTokenFromUrl, sleep, waitForDistributionToken } from '@/utils/embed'

const initAxios = Axios.create({
  timeout: 1800000
})

function setAuthorizationHeader(token) {
  const header = normalizeAuthHeader(token)
  if (header) {
    initAxios.defaults.headers.common['Authorization'] = header
  } else {
    delete initAxios.defaults.headers.common['Authorization']
  }
}

function isSuccessResponse(data) {
  return data?.success === true || data?.code === '0' || data?.code === 0
}

async function resolveSsoProfile(authHeader) {
  const { data } = await initAxios.get('/api/user-service/sso/me', {
    headers: { Authorization: authHeader },
    _skipAuthRetry: true
  })
  if (isSuccessResponse(data) && data?.data) {
    applyAuthSession({
      accessToken: getToken() || authHeader.replace(/^Bearer\s+/i, ''),
      username: data.data.username,
      userId: data.data.userId,
      realName: data.data.realName
    })
    return true
  }
  return false
}

async function obtainDistributionToken() {
  let token = readTokenFromUrl()
  if (token) {
    return token
  }
  token = getToken()
  if (token) {
    return token
  }
  return waitForDistributionToken()
}

/** 嵌入分销：使用父页面 Token，不再走 12306 本地登录 */
async function ensureDistributionAuth() {
  let token = await obtainDistributionToken()
  if (!token) {
    return null
  }

  applyDistributionSession(token)
  const authHeader = normalizeAuthHeader(token)
  setAuthorizationHeader(authHeader)

  try {
    const ok = await resolveSsoProfile(authHeader)
    if (ok) {
      return token
    }
  } catch (e) {
    // continue retry
  }

  // Token 可能稍后才写入 URL / postMessage，短暂等待后重试
  for (let i = 0; i < 8; i++) {
    await sleep(350)
    token = readTokenFromUrl() || getToken() || (await waitForDistributionToken(1200))
    if (!token) {
      continue
    }
    applyDistributionSession(token)
    const retryHeader = normalizeAuthHeader(token)
    setAuthorizationHeader(retryHeader)
    try {
      if (await resolveSsoProfile(retryHeader)) {
        return token
      }
    } catch (e) {
      // continue retry
    }
  }

  clearAuthSession()
  setAuthorizationHeader(null)
  return null
}

export async function ensureAuthenticated() {
  if (isEmbedMode()) {
    const existing = getToken()
    if (existing) {
      setAuthorizationHeader(existing)
      if (hasAuthSession()) {
        return existing
      }
    }
    return ensureDistributionAuth()
  }

  const existing = getToken()
  if (existing) {
    setAuthorizationHeader(existing)
    return existing
  }
  return null
}

initAxios.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = normalizeAuthHeader(token)
    }
    return config
  },
  (error) => Promise.reject(error)
)

initAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error
    if (!config || config._skipAuthRetry) {
      return Promise.reject(error)
    }
    if (response?.status === 401 && !config._retry && isEmbedMode()) {
      clearAuthSession()
      setAuthorizationHeader(null)
      const token = await ensureDistributionAuth()
      if (token) {
        config._retry = true
        config.headers.Authorization = normalizeAuthHeader(token)
        return initAxios(config)
      }
    }
    return Promise.reject(error)
  }
)

export default initAxios
export { setAuthorizationHeader }
